import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, type UseFormReset, type Control } from 'react-hook-form';
import dayjs from 'dayjs';
import type { AccrualProfileRow, FormValues } from "repository/timekeeper/@types/AccrualProfile";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type AccrualProfileRowWithIndex = AccrualProfileRow & { _index: number };

type UseAccrualProfileRowsProps = {
    employeeId: number;
    control: Control<FormValues>;
    reset: UseFormReset<FormValues>;
};

type UseAccrualProfileRowsReturn = {
    /** Fields de useFieldArray augmentados con _index */
    rows: AccrualProfileRowWithIndex[];
    /** Agrega una fila nueva vacía al final */
    addRow: () => void;
    /** Elimina la fila en la posición dada */
    removeRow: (index: number) => void;
    /** Inicializa las filas desde datos del backend.
     *  Si el array está vacío, inserta una fila vacía por defecto. */
    initRows: (backendRows: AccrualProfileRow[]) => void;
};

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

export const useAccrualProfileRows = ({
    employeeId,
    control,
    reset,
}: UseAccrualProfileRowsProps): UseAccrualProfileRowsReturn => {

    /**
     * Contador de IDs como ref — scoped al ciclo de vida del hook.
     * Evita el bug de la variable de módulo que persiste entre montajes (FIX #4).
     */
    const idCounterRef = useRef(0);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'profiles',
    });

    // ── Generación de fila nueva ──────────────────────────────

    /**
     * Genera una fila vacía con ID único y employee_accrual_profile_id = null.
     * null es obligatorio: indica al submit que esta fila va al endpoint POST,
     */
    const buildNewRow = useCallback((): AccrualProfileRow => {
        idCounterRef.current += 1;
        return {
            id: idCounterRef.current,
            employee_accrual_profile_id: null,
            employee_id: employeeId,
            accrual_profile_id: '',
            start_date: dayjs(),
            end_date: dayjs(),
        };
    }, [employeeId]);

    // ── API pública ───────────────────────────────────────────

    const addRow = useCallback(() => {
        append(buildNewRow());
    }, [append, buildNewRow]);

    const removeRow = useCallback((index: number) => {
        remove(index);
    }, [remove]);

    /**
     * Inicializa el form con filas provenientes del backend.
     *
     * - Si existen filas: las carga y sincroniza el contador de IDs
     *   con la cantidad recibida para que los IDs generados por addRow
     *   no colisionen con los IDs de las filas cargadas.
     *
     * - Si no existen filas: inserta una fila vacía por defecto
     *   para que el usuario no vea una grilla vacía.
     */
    const initRows = useCallback((backendRows: AccrualProfileRow[]) => {
        if (backendRows.length === 0) {
            idCounterRef.current = 1;
            reset({ profiles: [buildNewRow()] });
        } else {
            idCounterRef.current = backendRows.length;
            reset({ profiles: backendRows });
        }
    }, [reset, buildNewRow]);

    // ── Rows augmentadas ─────────────────────────────────────

    /**
     * Augmentamos fields con _index posicional estable.
     * DataGrid recibe esto como `rows` y cada renderCell accede
     * a params.row._index en O(1) en lugar de findIndex O(n). (FIX #6)
     *
     * Solo se recalcula cuando cambia la longitud o identidad de fields.
     */
    const rows = useMemo<AccrualProfileRowWithIndex[]>(
        () => fields.map((field, index) => ({ ...field, _index: index })),
        [fields],
    );

    return { rows, addRow, removeRow, initRows };
};